/* eslint-disable no-bitwise */

import { UntypedFormGroup } from "@angular/forms";
import { CellChange } from "handsontable";
import _ from "lodash";
import { Cellstyle } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc } from "~app/common/components/hot-doc/hotdoc/doc";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { Produktmatris } from "../../produktinstruktioner-details/matris/Produktmatris";
import { ProduktmatrisGrid } from "../../produktinstruktioner-details/matris/ProduktmatrisGrid";
import { EmptyCell } from "../../produktinstruktioner-details/matris/cells/emptyCell";
import { PrisCell } from "../../produktinstruktioner-details/matris/cells/prisCell";
import { RadprisNumberCell } from "../../produktinstruktioner-details/matris/cells/radprisNumberCell";
import { IDiameterklass } from "../IProduktdefinition";
import { ProduktdefinitionHeaderData } from "../ProduktdefinitionHeaderData";
import { LangdDiameterMatris } from "./LangdDiameterMatris";

export class Vardematris extends LangdDiameterMatris {
    showVardematrisPrisrad: boolean;

    constructor(grid: ProduktmatrisGrid,
        form: UntypedFormGroup,
        headerData: ProduktdefinitionHeaderData,
        canEdit: boolean) {
        super(grid, form, headerData, canEdit);
    }

    getVardematris(raskModalService: RaskModalService, form: UntypedFormGroup, showPrisrad: boolean, afterChanges: (changes: CellChange[]) => any): HotDoc {
        this.showVardematrisPrisrad = showPrisrad;

        const colFactory = () => this.buildColumnDefinitions({ readOnly: !this.canEdit,
            type: "numeric" });
        const rowFactory = (doc: HotDoc) => this.buildRowDefinitions(
            doc,
            {
                cellFactory: (pmData) => {
                    const prisCell = new PrisCell(pmData, form, Cellstyle.Default | Cellstyle.AlignCenter);
                    prisCell.enablePrisradAlert(this.showVardematrisPrisrad);
                    return prisCell;
                },
                columnSummaryFactory: (cells) => new EmptyCell(),
                rowSummaryFactory: (cells) => new EmptyCell(),
                sumOfSumsFactory: (cells) => new EmptyCell()
            }, raskModalService, this.showVardematrisPrisrad);
        const settingsFactory = (doc: HotDoc) => this.getBaseSettings(doc, raskModalService, {
            outsideClickDeselects: this.outsideClickDeselector,
            afterChange: (changes, source) => {
                afterChanges(changes);
            }
        });

        return new HotDoc(colFactory, rowFactory, settingsFactory);
    }

    setKapkriteria(hotDoc: HotDoc, kapkriteria: string, cells: any[]) {
        const produktmatriser = this.getCellsOfType(cells, hotDoc, PrisCell).map(x => x.prismatris);
        produktmatriser.forEach((cell) => {
            if (cell.kapkriteria !== kapkriteria) {
                cell.kapkriteria = kapkriteria;
                cell.markAsDirty();
                this.form.markAsDirty();
            }
        });

        hotDoc.rebind();
    }

    applyForPrismatris(hotDoc: HotDoc, cells: any[], callbackProduktmatris: (produktmatris: Produktmatris) => void, callbackRadpris: (produktmatris: IDiameterklass) => void) {
        const produktmatriser = this.getCellsOfType(cells, hotDoc, PrisCell).map(x => x.prismatris);
        const radpriser = this.getCellsOfType(cells, hotDoc, RadprisNumberCell).map(x => x.diameterklass);
        produktmatriser.forEach(callbackProduktmatris);
        radpriser.forEach(callbackRadpris);
    }

    applyPrisrader() {
        let dirty = false;
        this.grid.getCells().forEach(x => {
            if (x.pris !== x.radprisBeraknat) {
                x.pris = x.radprisBeraknat;
                x.dirty = true;
                dirty = true;
            }
        });

        if (dirty) {
            this.form.markAsDirty();
        }
    }

    setVardematrisPrisrad(hotDoc: HotDoc, value: boolean) {
        this.showVardematrisPrisrad = value;
        hotDoc.rebind();
    }

    refreshPrisradValue(absolut: boolean, utgangsvarde: number) {
        const calcMethod = absolut ? (radpris: number, justering: number) => radpris + justering :
            (radpris: number, justering) => radpris * (justering / 100);

        const withUtgangsvarde = (value) => Math.max(0, Math.round(value + (utgangsvarde ?? 0)));

        const existerandeJusteringar = this.grid.diameterklasser
            .filter(x => x.radprisJustering.length > 0)
            .map(x => ({ startdiameter: x.startdiameter,
                radprisJustering: x.radprisJustering }));

        let diffCount = 0;

        this.grid.diameterklasser
            .forEach(dk => {
                // Hitta den diameter som antingen är "närmast" den itererande diameterklassen.
                // Antingen är det den själv eller den som den ska ärva från
                const justeringsvardenForDiameterklass = _.findLast(existerandeJusteringar, y => dk.startdiameter >= y.startdiameter);
                this.grid.langdklasser.forEach(lk => {
                    const cell = this.grid.getCell(lk.startlangd, dk.startdiameter);
                    if (!justeringsvardenForDiameterklass) {
                        cell.radprisBeraknat = withUtgangsvarde(dk.radpris);

                    } else {
                        const justeratRadpris = justeringsvardenForDiameterklass.radprisJustering.find(f => f.startlangd === lk.startlangd);
                        if (justeratRadpris) {
                            cell.radprisBeraknat = withUtgangsvarde(calcMethod(dk.radpris, justeratRadpris.justering));
                        } else {
                            cell.radprisBeraknat = withUtgangsvarde(dk.radpris);
                        }
                    }
                    if (cell.pris !== cell.radprisBeraknat) {
                        diffCount++;
                    }
                });
            });

        this.antalDiffandeRadpriser = diffCount;
    }
}
